// H8/H9 (#696/#663) — preserve the device-held QUARANTINE
// (`orphaned_capture_items`) to the durable server recovery store on sign-out,
// then AUDIT the outcome. Split out of auth-provider.tsx (like the queue rescue's
// preserve-upload-queue-audit.ts and the Tier-3 stash's stash-held-work-audit.ts)
// to keep that file under the 500-line gate and so the bounded rescue + audit is
// unit-testable without rendering the whole provider.
//
// Best-effort and bounded: never throws, never traps logout. The audit is NEVER
// silent — a sign-out that clears contractual counts off a shared device records
// what it preserved and what the clear will discard, so a deliberate discard is
// forensically visible. Wired into proceedLogout() on the ONLINE branch, followed
// by the ps_crud queue rescue (preserve-upload-queue-audit.ts).

import * as Sentry from '@sentry/react'

import {
  heldCaptureTotal,
  preserveHeldCapturesBeforeClear,
  type HeldCaptureLoad,
} from '../hooks/powersync/logout-cleanup'
import type { RescueResult } from '../hooks/powersync/rescue-held-captures'

// Upper bound on the pre-clear rescue (a local SELECT + a POST to the recovery
// inbox). Like the clear timeout, this only trips for a wedged worker or a
// stalled network; on timeout we audit and proceed with the clear, never
// trapping the surveyor on a shared device.
const HELD_RESCUE_TIMEOUT_MS = 6_000

/**
 * Preserve the device-held quarantine to the durable server recovery store BEFORE
 * the destructive logout clear wipes it, then audit. `load` is the pre-clear held
 * probe — used for the audit copy and to decide whether the clear also discards a
 * ps_crud queue (which a sibling rescue ships). Best-effort and bounded: never
 * throws, never traps logout.
 */
export async function preserveHeldThenAudit(load: HeldCaptureLoad): Promise<void> {
  // `result` stays null on BOTH the timeout arm (the race resolves null) and a
  // thrown rescue (the catch leaves the initializer untouched, since the failed
  // `await` never assigns), so `result !== null` precisely means "the rescue
  // resolved" — no separate `failed` flag is needed (it would only describe an
  // unreachable result-non-null-but-failed quadrant).
  let result: RescueResult | null = null
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    result = await Promise.race<RescueResult | null>([
      preserveHeldCapturesBeforeClear(),
      new Promise<null>((resolve) => {
        timer = setTimeout(() => {
          resolve(null)
        }, HELD_RESCUE_TIMEOUT_MS)
      }),
    ])
  } catch {
    // Best-effort: a failed rescue (offline / server error / read error) leaves
    // `result` null and is audited below as an at-risk discard, never swallowed.
  } finally {
    if (timer !== undefined) clearTimeout(timer)
  }

  const total = heldCaptureTotal(load)
  // The rescue only ships the QUARANTINE dimension. The ps_crud upload queue
  // (the H10 dimension) is not rescued — the clear discards it. So the audit
  // severity must key on whether a DISCARD actually occurred, NOT on whether the
  // rescue succeeded: a queued-only sign-out (quarantine empty, queue non-empty)
  // rescues nothing and would otherwise file at 'info' "preserved 0/0" while the
  // clear destroys real contractual counts — burying the loss the audit exists
  // to surface (audit PR #779 / data-loss lens). `uploadQueueCount` is known
  // only for a 'known' load; an indeterminate device could hold an unverifiable
  // queue, so it never qualifies as a clean preserve.
  const queued = load.status === 'known' ? load.uploadQueueCount : null
  if (result !== null && queued === 0) {
    // Quarantine durably preserved AND no queued writes held — a clean sign-out.
    Sentry.captureMessage(
      `logout: preserved ${result.sent}/${result.found} device-held capture ` +
        `count(s) to the recovery store before clearing; no queued writes held`,
      'info',
    )
  } else if (result !== null) {
    // Quarantine preserved; the clear wipes the ps_crud queue from THIS device.
    // Those queued writes are separately rescued to the recovery store by the
    // F3 queue rescue that follows (preserveUploadQueueThenAudit) when online, so
    // this is NOT a silent loss — but a sign-out wiping a queue is notable, so it
    // stays 'warning'. The "rescued … where online" clause reconciles this with
    // the queue-rescue audit so an operator filtering warnings does not misread
    // it as loss (audit eh-2 / cd-D). 'an unverifiable number of' on indeterminate.
    const discarded = queued === null ? 'an unverifiable number of' : `${queued}`
    Sentry.captureMessage(
      `logout: preserved ${result.sent}/${result.found} quarantined count(s) to ` +
        `the recovery store; the clear discards ${discarded} queued ` +
        `change(s) from ps_crud — rescued to the recovery store where online ` +
        `(see the queue-rescue audit) (held total ${total ?? 'indeterminate'})`,
      'warning',
    )
  } else {
    // The rescue failed/timed out — the quarantine itself is discarded too.
    Sentry.captureMessage(
      `logout: could NOT preserve device-held capture counts to the recovery ` +
        `store before clearing (held total ${total ?? 'indeterminate'}); signing ` +
        `out anyway — held counts will be discarded from this device`,
      'warning',
    )
  }
}
